import { useEffect, useRef, useState } from 'react';
import { HeadphoneOff, Headphones, Maximize2, Mic, MicOff, Minimize2, MonitorOff, MonitorUp, Phone, PhoneOff, Video, VideoOff, Volume2 } from 'lucide-react';
import { Permission, type VoiceState } from '@spawnwire/shared';
import { Avatar, Button, clsx, ContextMenu, Spinner, Tooltip } from '../../components/ui';
import { can, useChannelPermissions } from '../../lib/perms';
import { joinVoice, leaveVoice, setUserVolume, toggleCamera, toggleDeafen, toggleMute, toggleScreenShare, useVoice } from '../../lib/voice';
import { displayNameIn, useStore } from '../../store/store';
import { userMenu } from '../members/userMenu';

function VideoEl({ stream, muted = true, mirror, contain }: { stream: MediaStream; muted?: boolean; mirror?: boolean; contain?: boolean }) {
  const ref = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    if (ref.current && ref.current.srcObject !== stream) ref.current.srcObject = stream;
  }, [stream]);
  return <video ref={ref} autoPlay playsInline muted={muted} className={clsx('size-full bg-black', contain ? 'object-contain' : 'object-cover', mirror && '-scale-x-100')} />;
}

interface Tile {
  key: string;
  userId: string;
  kind: 'camera' | 'screen' | 'avatar';
  stream: MediaStream | null;
  local: boolean;
}

function TileView({ tile, state, spaceId, channelId, focused, onFocus }: { tile: Tile; state: VoiceState | undefined; spaceId: string | null; channelId: string; focused: boolean; onFocus: () => void }) {
  const user = useStore((s) => s.users[tile.userId]);
  const name = useStore((s) => displayNameIn(s, tile.userId, spaceId));
  const speaking = useVoice((v) => !!v.speaking[tile.userId]);
  const connection = useVoice((v) => v.remote[tile.userId]?.connection);
  const relayFailed = useVoice((v) => !!v.remote[tile.userId]?.relayFailed);
  const volume = useVoice((v) => v.userVolumes[tile.userId] ?? 100);
  const mutedNow = state?.selfMute || state?.serverMute;
  return (
    <ContextMenu entries={userMenu(tile.userId, spaceId, { voiceChannelId: channelId })}>
      <div
        className={clsx(
          'group relative flex aspect-video min-h-0 items-center justify-center overflow-hidden rounded-xl bg-sidebar ring-2 transition-shadow',
          speaking && !mutedNow ? 'ring-success' : 'ring-transparent',
          focused && 'col-span-full',
        )}
      >
        {tile.stream && tile.kind !== 'avatar' ? (
          <VideoEl stream={tile.stream} mirror={tile.local && tile.kind === 'camera'} contain={tile.kind === 'screen'} />
        ) : (
          <Avatar user={user} size={focused ? 112 : 72} speaking={speaking && !mutedNow} />
        )}
        <div className="absolute bottom-2 left-2 flex max-w-[80%] items-center gap-1.5 rounded-md bg-black/60 px-2 py-1 text-xs font-medium text-white">
          {tile.kind === 'screen' && <MonitorUp className="size-3.5" aria-hidden />}
          <span className="truncate">
            {name}
            {tile.local ? ' (you)' : ''}
          </span>
          {mutedNow && <MicOff className={clsx('size-3.5', state?.serverMute && 'text-danger')} aria-label="Muted" />}
          {(state?.selfDeaf || state?.serverDeaf) && <HeadphoneOff className="size-3.5" aria-label="Deafened" />}
        </div>
        {!tile.local && connection && connection !== 'connected' && (
          <div className="absolute right-2 top-2 flex items-center gap-1 rounded-md bg-black/60 px-2 py-1 text-[11px] text-white">
            {relayFailed ? "Can't reach voice relay" : connection === 'failed' || connection === 'disconnected' ? 'Reconnecting' : <><Spinner className="size-3 text-white" /> Connecting</>}
          </div>
        )}
        {(tile.kind === 'screen' || tile.kind === 'camera') && (
          <button type="button" onClick={onFocus} aria-label={focused ? 'Exit focus' : 'Focus'} className="absolute right-2 top-2 hidden rounded-md bg-black/60 p-1.5 text-white group-hover:block">
            {focused ? <Minimize2 className="size-4" /> : <Maximize2 className="size-4" />}
          </button>
        )}
        {!tile.local && tile.kind !== 'screen' && (
          <label className="absolute bottom-2 right-2 hidden items-center gap-1 rounded-md bg-black/60 px-2 py-1 text-white group-hover:flex">
            <Volume2 className="size-3.5" aria-hidden />
            <input type="range" min={0} max={200} value={volume} onChange={(e) => setUserVolume(tile.userId, Number(e.target.value))} className="w-20 accent-[var(--p-accent)]" aria-label={`Volume for ${name}`} />
          </label>
        )}
      </div>
    </ContextMenu>
  );
}

export function CallControls({ compact }: { compact?: boolean }) {
  const muted = useVoice((v) => v.muted);
  const deafened = useVoice((v) => v.deafened);
  const video = useVoice((v) => v.video);
  const screen = useVoice((v) => v.screen);
  const channelId = useVoice((v) => v.channelId);
  const perms = useChannelPermissions(channelId);
  const meId = useStore((s) => s.me?.id);
  // On a stage, the audience can only listen.
  const audience = useStore((s) => (channelId && meId ? !!s.voiceStates[channelId]?.[meId]?.suppress : false));
  const canVideo = can(perms, Permission.STREAM) && !audience;
  const canScreen = can(perms, Permission.SCREEN_SHARE) && !audience;
  const btn = (active: boolean, danger = false) =>
    clsx('flex size-12 items-center justify-center rounded-full transition-colors', danger ? 'bg-danger text-white hover:brightness-110' : active ? 'bg-fg text-main' : 'bg-active text-fg hover:bg-line-strong', compact && 'size-10');
  return (
    <div className="flex items-center justify-center gap-3" role="toolbar" aria-label="Call controls">
      <Tooltip label={audience ? 'You are in the audience' : muted ? 'Unmute (Ctrl Shift M)' : 'Mute (Ctrl Shift M)'}>
        <button type="button" disabled={audience} className={clsx(btn(muted || audience), 'disabled:opacity-40')} onClick={toggleMute} aria-pressed={muted} aria-label={muted ? 'Unmute' : 'Mute'}>
          {muted || audience ? <MicOff className="size-5" /> : <Mic className="size-5" />}
        </button>
      </Tooltip>
      <Tooltip label={deafened ? 'Undeafen' : 'Deafen'}>
        <button type="button" className={btn(deafened)} onClick={toggleDeafen} aria-pressed={deafened} aria-label={deafened ? 'Undeafen' : 'Deafen'}>
          {deafened ? <HeadphoneOff className="size-5" /> : <Headphones className="size-5" />}
        </button>
      </Tooltip>
      <Tooltip label={canVideo ? (video ? 'Turn off camera' : 'Turn on camera') : 'You cannot turn on your camera here'}>
        <button type="button" disabled={!canVideo} className={clsx(btn(video), 'disabled:opacity-40')} onClick={() => void toggleCamera()} aria-pressed={video} aria-label="Camera">
          {video ? <Video className="size-5" /> : <VideoOff className="size-5" />}
        </button>
      </Tooltip>
      <Tooltip label={canScreen ? (screen ? 'Stop sharing' : 'Share your screen') : 'You cannot share your screen here'}>
        <button type="button" disabled={!canScreen} className={clsx(btn(screen), 'disabled:opacity-40')} onClick={() => void toggleScreenShare()} aria-pressed={screen} aria-label="Screen share">
          {screen ? <MonitorOff className="size-5" /> : <MonitorUp className="size-5" />}
        </button>
      </Tooltip>
      <Tooltip label="Leave call">
        <button type="button" className={btn(false, true)} onClick={() => void leaveVoice()} aria-label="Leave call">
          <PhoneOff className="size-5" />
        </button>
      </Tooltip>
    </div>
  );
}

function useTiles(channelId: string): { tiles: Tile[]; states: Record<string, VoiceState> } {
  const states = useStore((s) => s.voiceStates[channelId]) ?? {};
  const meId = useStore((s) => s.me?.id);
  const remote = useVoice((v) => v.remote);
  const localCamera = useVoice((v) => v.localCamera);
  const localScreen = useVoice((v) => v.localScreen);
  const connected = useVoice((v) => v.channelId === channelId);
  const tiles: Tile[] = [];
  for (const st of Object.values(states).sort((a, b) => a.joinedAt.localeCompare(b.joinedAt))) {
    const local = st.userId === meId;
    const media = local ? { camera: localCamera, screen: localScreen } : remote[st.userId];
    if (st.screen && media?.screen && (connected || local)) tiles.push({ key: `${st.userId}:screen`, userId: st.userId, kind: 'screen', stream: media.screen, local });
    const camera = st.video && media?.camera && (connected || local) ? media.camera : null;
    tiles.push({ key: `${st.userId}:main`, userId: st.userId, kind: camera ? 'camera' : 'avatar', stream: camera, local });
  }
  return { tiles, states };
}

/** Full-height call view for voice channels. */
export function VoiceView({ channelId }: { channelId: string }) {
  const connectedHere = useVoice((v) => v.channelId === channelId);
  const status = useVoice((v) => v.status);
  const channel = useStore((s) => s.channels[channelId]);
  const perms = useChannelPermissions(channelId);
  const { tiles, states } = useTiles(channelId);
  const [focus, setFocus] = useState<string | null>(null);
  const spaceId = channel?.spaceId ?? null;
  const canConnect = can(perms, Permission.CONNECT);
  const focusedTile = tiles.find((t) => t.key === focus);
  const shown = focusedTile ? [focusedTile, ...tiles.filter((t) => t.key !== focus)] : tiles;

  return (
    <div className="flex min-h-0 flex-1 flex-col bg-rail">
      <div className="scroll-thin min-h-0 flex-1 overflow-y-auto p-4">
        {tiles.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center gap-3 text-center">
            <Volume2 className="size-12 text-faint" aria-hidden />
            <h2 className="text-xl font-semibold text-fg">{channel?.name}</h2>
            <p className="text-sm text-muted">No one is here yet.</p>
          </div>
        ) : (
          <div className={clsx('grid gap-3', shown.length === 1 ? 'grid-cols-1' : shown.length <= 4 ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-2 lg:grid-cols-3')}>
            {shown.map((t) => (
              <TileView key={t.key} tile={t} state={states[t.userId]} spaceId={spaceId} channelId={channelId} focused={t.key === focus} onFocus={() => setFocus(focus === t.key ? null : t.key)} />
            ))}
          </div>
        )}
      </div>
      <div className="shrink-0 border-t border-line bg-sidebar p-4">
        {connectedHere ? (
          status === 'connecting' ? (
            <div className="flex justify-center gap-2 text-sm text-muted">
              <Spinner className="size-4" /> Connecting
            </div>
          ) : (
            <CallControls />
          )
        ) : (
          <div className="flex justify-center">
            <Button size="lg" icon={Phone} disabled={!canConnect} onClick={() => void joinVoice(channelId)}>
              {canConnect ? 'Join voice' : 'You cannot join this channel'}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

/** Compact call area shown above the messages of a conversation. */
export function CallPanel({ channelId }: { channelId: string }) {
  const connectedHere = useVoice((v) => v.channelId === channelId);
  const { tiles, states } = useTiles(channelId);
  const [focus, setFocus] = useState<string | null>(null);
  return (
    <div className="shrink-0 border-b border-line bg-rail p-3">
      <div className="mx-auto grid max-w-4xl gap-3" style={{ gridTemplateColumns: `repeat(${Math.min(Math.max(tiles.length, 1), 3)}, minmax(0, 1fr))` }}>
        {tiles.map((t) => (
          <TileView key={t.key} tile={t} state={states[t.userId]} spaceId={null} channelId={channelId} focused={t.key === focus} onFocus={() => setFocus(focus === t.key ? null : t.key)} />
        ))}
      </div>
      <div className="mt-3 flex justify-center">
        {connectedHere ? (
          <CallControls compact />
        ) : (
          <div className="flex items-center gap-3">
            <span className="text-sm text-muted">A call is in progress.</span>
            <Button icon={Phone} onClick={() => void joinVoice(channelId)}>
              Join call
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
